import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Form, Badge, Alert, Table, Spinner } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../hooks';
import { shiftPreferenceAPI } from '../../services';
import { toast } from 'react-toastify';
import EmployeeWeekendHistoryModal from './EmployeeWeekendHistoryModal';

function MetBadge({ met }) {
  return met ? (
    <Badge bg="success">✓ Đạt</Badge>
  ) : (
    <Badge bg="danger">✗ Chưa đạt</Badge>
  );
}

// #131343 — NV trong 30 ngày đầu được miễn toàn bộ điều kiện đăng ký ca (không bị trừ
// TLT, không bị chặn đăng ký). Số liệu bên dưới GIỮ NGUYÊN để quản lý vẫn theo dõi được,
// badge này chỉ để không hiểu nhầm "✗ Chưa đạt" là vi phạm cố ý.
function NewHireBadge({ isNewHire }) {
  if (!isNewHire) return null;
  return (
    <Badge bg="info" text="dark" className="ms-1">
      🌱 NV mới (miễn trừ)
    </Badge>
  );
}

function ConsecutiveBadge({ count }) {
  if (count === 0) return <span className="text-muted">—</span>;
  return (
    <Badge bg={count >= 2 ? 'danger' : 'warning'} text={count >= 2 ? 'white' : 'dark'}>
      {count} tuần
    </Badge>
  );
}

export default function EmployeeWeekendTrackingPage() {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [onlyUnmet, setOnlyUnmet] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState(null);

  const loadData = async () => {
    if (!selectedBranch?.id) return;
    setLoading(true);
    try {
      const res = await shiftPreferenceAPI.getWeekendTracking({
        branch_id: selectedBranch.id,
        only_unmet: onlyUnmet,
      });
      setData(Array.isArray(res) ? res : res?.data ?? []);
    } catch (err) {
      toast.error(err?.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedBranch?.id, onlyUnmet]);

  const totalEmployees = data.length;
  const unmetWeekend = data.filter((r) => !r.meets_weekend_requirement).length;
  const flagged2Weeks = data.filter((r) => r.consecutive_unmet_weeks >= 2).length;

  return (
    <PageLayout title="Theo dõi ca cuối tuần nhân viên">
      {/* Summary cards */}
      <Row className="mb-4 g-3">
        <Col xs={12} md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-2 fw-bold text-primary">{totalEmployees}</div>
              <div className="text-muted small">Tổng NV active</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-2 fw-bold text-danger">{unmetWeekend}</div>
              <div className="text-muted small">Chưa đạt điều kiện cuối tuần</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-2 fw-bold text-warning">{flagged2Weeks}</div>
              <div className="text-muted small">Cờ ≥2 tuần liên tục không đạt</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white d-flex align-items-center gap-3 flex-wrap py-3">
          <span className="fw-semibold">Danh sách nhân viên — Tuần sau</span>
          <Form.Check
            type="switch"
            id="only-unmet-switch"
            label="Chỉ hiện chưa đạt"
            checked={onlyUnmet}
            onChange={(e) => setOnlyUnmet(e.target.checked)}
            className="ms-auto"
          />
          <Button variant="outline-secondary" size="sm" onClick={loadData} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : 'Làm mới'}
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : data.length === 0 ? (
            <Alert variant="info" className="m-3">
              {onlyUnmet ? 'Tất cả nhân viên đã đạt điều kiện ca cuối tuần.' : 'Không có dữ liệu.'}
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Nhân viên</th>
                    <th className="text-center">Telegram</th>
                    <th className="text-center">Ca cuối tuần</th>
                    <th className="text-center">Ca tối CT</th>
                    <th className="text-center">Trạng thái</th>
                    <th className="text-center">Liên tục KĐ</th>
                    <th className="text-center">Lịch sử</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => {
                    const weekendMissing = Math.max(0, row.weekend_required - row.weekend_count);
                    const eveningMissing = Math.max(0, row.weekend_evening_required - row.weekend_evening_count);
                    return (
                      <tr
                        key={row.employee_id}
                        className={!row.meets_weekend_requirement ? 'table-danger bg-opacity-25' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setHistoryEmployee(row)}
                      >
                        <td>
                          <div className="fw-semibold">{row.full_name}</div>
                          <div className="text-muted small">{row.employee_id.slice(0, 8)}…</div>
                        </td>
                        <td className="text-center">
                          {row.telegram_id ? (
                            <Badge bg="success" pill>✓</Badge>
                          ) : (
                            <Badge bg="secondary" pill>✗</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={row.weekend_count >= row.weekend_required ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                            {row.weekend_count}/{row.weekend_required}
                          </span>
                          {weekendMissing > 0 && (
                            <div className="text-danger" style={{ fontSize: '0.72rem' }}>thiếu {weekendMissing}</div>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={row.weekend_evening_count >= row.weekend_evening_required ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                            {row.weekend_evening_count}/{row.weekend_evening_required}
                          </span>
                          {eveningMissing > 0 && (
                            <div className="text-danger" style={{ fontSize: '0.72rem' }}>thiếu {eveningMissing}</div>
                          )}
                        </td>
                        <td className="text-center">
                          <MetBadge met={row.meets_weekend_requirement} />
                          <NewHireBadge isNewHire={row.is_new_hire_grace} />
                        </td>
                        <td className="text-center">
                          <ConsecutiveBadge count={row.consecutive_unmet_weeks} />
                        </td>
                        <td className="text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHistoryEmployee(row);
                            }}
                          >
                            Xem
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <EmployeeWeekendHistoryModal
        show={!!historyEmployee}
        onHide={() => setHistoryEmployee(null)}
        employee={historyEmployee}
        branchId={selectedBranch?.id}
      />
    </PageLayout>
  );
}
